import React, { CSSProperties, PropsWithChildren, useRef } from 'react';
import styled from 'styled-components';
import {
  ResponsiveBreakpointsCtx,
  tabletBreakpoint,
  defaultBreakpoints,
} from 'WindowDimensionProvider';
import { ResourceMap } from 'components/Resource';
import useResourceBookingSession from 'features/Reserve/hooks/useResourceBookingSession';
import { isResourceAvailable } from 'features/Reserve/utils/groupAvailability';
import getMapMedia from 'utils/data/medias/getMapMedia';
import { MediaLinkResolution } from '@kouto/types';
import { ResourceMapContextProvider } from '../contexts/ResourceMapContext';

interface Props extends ContainerOptions {}

export const TwoColumnMapContainer = ({
  children,
  ...containerOptions
}: PropsWithChildren<Props>) => {
  const breakpoints = useRef({
    ...defaultBreakpoints,
    mobile: 1024,
  }).current;
  const {
    continuousSelection,
    duration,
    isContinuous,
    listing,
    resourceGroup,
    resourceId: experienceId,
    sessions,
    time,
  } = useResourceBookingSession();

  if (!listing) {
    return null;
  }

  const mapMedia = getMapMedia(listing?.medias ?? []);
  const isMapped = listing?.isMapped ?? false;

  if (!isMapped) {
    return (
      <ResponsiveBreakpointsCtx.Provider value={breakpoints}>
        {children}
      </ResponsiveBreakpointsCtx.Provider>
    );
  }

  const isAvailable = (resourceId: string) =>
    isResourceAvailable({
      continuousSelection,
      duration,
      isContinuous,
      resourceId,
      sessions,
      time,
    });

  let resourcesGroupsToShowInMap = resourceGroup
    ? [resourceGroup]
    : listing.resourceGroups ?? [];
  resourcesGroupsToShowInMap = resourcesGroupsToShowInMap.map((group) => ({
    ...group,
    experiences: experienceId
      ? group.experiences.filter((exp) => isAvailable(exp.id))
      : group.experiences.map((exp) => ({
          ...exp,
          unavailable: !isAvailable(exp.id),
        })),
  }));

  return (
    <ResponsiveBreakpointsCtx.Provider value={breakpoints}>
      <ResourceMapContextProvider>
        <Container {...containerOptions}>
          {children}
          <div className="map-container">
            <ResourceMap
              assetUrl={
                mapMedia?.links.find(
                  (link) => link.resolution === MediaLinkResolution.ORIGINAL,
                )?.url ?? ''
              }
              resourceGroups={resourcesGroupsToShowInMap}
              collectionName={listing.title ?? undefined}
            />
          </div>
        </Container>
      </ResourceMapContextProvider>
    </ResponsiveBreakpointsCtx.Provider>
  );
};

interface ContainerOptions {
  gap?: CSSProperties['gap'];
  width?: CSSProperties['width'];
}

const Container = styled.div<ContainerOptions>`
  display: flex;
  justify-content: space-between;
  flex-direction: column-reverse;
  gap: ${(props) => props.gap ?? '24px'};

  --custom-mobile-breakpoint: ${tabletBreakpoint};

  > * {
    width: 100% !important;
  }

  .map-container {
    height: 281px;
    user-select: none;
  }

  @media all and (min-width: ${tabletBreakpoint}) {
    display: grid;
    grid-template-columns: ${(props) => props.width || 'minmax(300px, 400px)'} 1fr;

    .map-container {
      position: sticky;
      height: 650px;
      top: 20px;
    }
  }
`;
